/**
 * SG Incorporation E-Filing — data loader.
 *
 * Loads, for a given task/incorporation:
 *   1. the stored incorporation form  → `GET /api/aiDocuments/incorporation/entity-data/:icpCd`
 *   2. the uploaded files (passport / proof / allotment / RC docs)
 *                                      → `GET /api/setupEntity/incorporation/detail/:icpCd`
 *   3. the generated documents (Form 45, Notices, CS Profile, …)
 *                                      → `GET /api/aiDocuments/incorporation/load-state/:taskId`
 * and seeds the editable {@link IncorporationEfilingModel}.
 */
import { reactive, ref } from 'vue';
import { extractSgFormFromIncorporationData } from '@/features/setupEntity/utils/sgIncorporationPayload';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import {
  createEmptyModel,
  seedEfilingModel,
  buildBizfileCreatePayload,
  type IncorporationEfilingModel,
} from './incorporationEfilingModel';
import type { SgIncorporationForm } from '@/features/setupEntity/components/sg/types/sgIncorporation.types';

/** 코드→BIGO1 매핑 (idType: B03, 전화국가코드: B05). 화면·페이로드 모두 BIGO1 값으로 통일. */
interface CodeMap {
  byCode: Record<string, string>;
  byName: Record<string, string>;
  valSet: Set<string>;
}

async function buildCodeMap(mainCode: string, pick: (c: any) => string): Promise<CodeMap> {
  const map: CodeMap = { byCode: {}, byName: {}, valSet: new Set() };
  try {
    const resp: any = await baseCodeApi.getCodesByMainCode('CM', mainCode, undefined);
    const codes = resp?.codes || resp?.data?.codes || [];
    for (const c of codes) {
      const total = String(c.totalCode || c.TOTAL_CODE || '').trim();
      const name = String(
        c.defaultCodeName || c.DEFAULT_CODE_NAME || c.codeName || c.CODE_NAME || '',
      ).trim();
      const val = String(pick(c) || '').trim();
      if (!val) continue;
      if (total) map.byCode[total] = val;
      if (name) map.byName[name.toUpperCase()] = val;
      map.valSet.add(val.toUpperCase());
    }
  } catch (e) {
    console.warn(`[IncorporationEfiling] base code ${mainCode} load failed:`, e);
  }
  return map;
}

/** raw(코드/이름/이미BIGO1) → BIGO1 값. 못 찾으면 원본 유지. */
function toBigo1(map: CodeMap, raw: string): string {
  const v = String(raw || '').trim();
  if (!v) return '';
  if (map.byCode[v]) return map.byCode[v];
  const up = v.toUpperCase();
  if (map.valSet.has(up)) return v;
  if (map.byName[up]) return map.byName[up];
  return v;
}

interface CodeMaps {
  idType: CodeMap; // B03 → BIGO1 (NRIC-C/NRIC-PR/FIN/PP)
  nationality: CodeMap; // B04 → ISO2 (KR …)
  country: CodeMap; // B05 → dial code (+82 …)
}

/**
 * 공통코드 맵 로드. 화면 콤보박스는 코드(B03/B04/B05 TOTAL_CODE)로 바인딩하고,
 * bizfile 제출 페이로드에서만 이 맵으로 BIGO 값(PP/KR/+82)으로 변환한다.
 */
async function loadCodeMaps(): Promise<CodeMaps> {
  const [idType, nationality, country] = await Promise.all([
    buildCodeMap('B03', (c) => c.bigo1 || c.BIGO1 || ''),
    buildCodeMap('B04', (c) => {
      const cands = [c.bigo1, c.BIGO1, c.bigo3, c.BIGO3, c.bigo2, c.BIGO2].map((x) =>
        String(x || '').trim(),
      );
      const iso = cands.find((x) => /^[A-Za-z]{2}$/.test(x));
      return iso || cands.find(Boolean) || '';
    }),
    buildCodeMap('B05', (c) => {
      // bizfile 은 국가번호를 "+" 없이 숫자로 받는다 ("82"/"65"). +82 → 82.
      const b1 = String(c.bigo1 || c.BIGO1 || '').trim();
      const b3 = String(c.bigo3 || c.BIGO3 || '').trim();
      const dial = /^\+?\d+$/.test(b1) ? b1 : /^\+?\d+$/.test(b3) ? b3 : b1 || b3;
      return dial.replace(/^\+/, '');
    }),
  ]);
  return { idType, nationality, country };
}

/** 모델을 복제 후 idType(B03)/nationality(B04)/전화국가코드(B05)를 BIGO 값으로 변환한 사본 반환. */
function convertCodesForPayload(
  model: IncorporationEfilingModel,
  maps: CodeMaps,
): IncorporationEfilingModel {
  const m = JSON.parse(JSON.stringify(model)) as IncorporationEfilingModel;
  for (const h of m.positionHolders) {
    if (h.category === '1') h.idType = toBigo1(maps.idType, h.idType);
    h.nationality = toBigo1(maps.nationality, h.nationality);
    h.mobileCountryCode = toBigo1(maps.country, h.mobileCountryCode);
  }
  for (const n of m.nominators) {
    n.idType = toBigo1(maps.idType, n.idType);
    n.nationality = toBigo1(maps.nationality, n.nationality);
    n.countryCode = toBigo1(maps.country, n.countryCode);
  }
  for (const c of m.controllers) {
    c.idType = toBigo1(maps.idType, c.idType);
    c.nationality = toBigo1(maps.nationality, c.nationality);
    c.countryCode = toBigo1(maps.country, c.countryCode);
  }
  return m;
}

/** An uploaded file row (from L_INCORPORATIONS_FILES). */
export interface EfUploadedFile {
  fileCd: string;
  fileType: string; // PASSPORT | ADDR_PROF | ALLOT_DOC | RC_DOC
  fileOwnerType: string; // DIRECTOR | SECRETAR | SHAREHOL | CONTROLR | SHARECAP
  fileOwnerCd: string;
  fileName: string;
  fileSize: number;
  contentType: string;
  /** download URL. */
  url: string;
}

/** A generated document row (from L_DOC_CUST_SAVE). */
export interface EfGeneratedDoc {
  saveId: number;
  templateId: number;
  fileName: string;
  fileSize: number;
  docStatus: string;
  url: string;
}

export interface UseIncorporationEfilingState {
  loading: boolean;
  error: string;
  loaded: boolean;
  icpCd: string;
  companyName: string;
  model: IncorporationEfilingModel;
  uploadedFiles: EfUploadedFile[];
  generatedDocs: EfGeneratedDoc[];
}

async function getJson(url: string): Promise<any> {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

async function putJson(url: string, body: unknown): Promise<any> {
  const res = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export function useIncorporationEfiling() {
  const state = reactive<UseIncorporationEfilingState>({
    loading: false,
    error: '',
    loaded: false,
    icpCd: '',
    companyName: '',
    model: createEmptyModel(),
    uploadedFiles: [],
    generatedDocs: [],
  });

  const rawIncorporationData = ref<Record<string, unknown> | null>(null);
  const codeMaps = ref<CodeMaps | null>(null);

  async function loadEntityData(icpCd: string): Promise<void> {
    const json = await getJson(`/api/aiDocuments/incorporation/entity-data/${encodeURIComponent(icpCd)}`);
    const data = json?.data ?? json;
    const incData = (data?.incorporationData ?? null) as Record<string, unknown> | null;
    rawIncorporationData.value = incData;
    state.companyName = String(data?.companyName || '').trim();

    const form = extractSgFormFromIncorporationData(incData);
    if (form) {
      state.model = seedEfilingModel(form, { companyName: state.companyName });
    } else {
      state.model = createEmptyModel();
      state.model.entityInfo.companyName = state.companyName;
    }
    // 모델은 코드(B03/B04/B05) 그대로 유지 — 콤보박스 바인딩용. 제출 시 buildPayload 에서 변환.
  }

  async function loadUploadedFiles(icpCd: string): Promise<void> {
    try {
      const json = await getJson(`/api/setupEntity/incorporation/detail/${encodeURIComponent(icpCd)}`);
      const files = (json?.files || []) as any[];
      state.uploadedFiles = files.map((f) => ({
        fileCd: String(f.fileCd || f.FILE_CD || ''),
        fileType: String(f.fileType || f.FILE_TYPE || ''),
        fileOwnerType: String(f.fileOwnerType || f.FILE_OWNER_TYPE || ''),
        fileOwnerCd: String(f.fileOwnerCd || f.FILE_OWNER_CD || ''),
        fileName: String(f.fileName || f.FILE_NAME || ''),
        fileSize: Number(f.fileSize || f.FILE_SIZE || 0),
        contentType: String(f.contentType || f.CONTENT_TYPE || ''),
        url: `/api/setupEntity/incorporation/file/${encodeURIComponent(String(f.fileCd || f.FILE_CD || ''))}`,
      }));
    } catch (e) {
      // uploaded-file listing is best-effort — never blocks the review screen.
      console.warn('[IncorporationEfiling] uploaded file list failed:', e);
      state.uploadedFiles = [];
    }
  }

  /** load-state 응답의 documents → state.generatedDocs */
  function applyGeneratedDocs(json: any): void {
    const docs = (json?.documents || []) as any[];
    state.generatedDocs = docs.map((d) => ({
      saveId: Number(d.saveId ?? d.SAVE_ID ?? 0),
      templateId: Number(d.templateId ?? d.TMP_ID ?? 0),
      fileName: String(d.fileName || d.FILE_NAME || ''),
      fileSize: Number(d.fileSize || d.FILE_SIZE || 0),
      docStatus: String(d.docStatus || d.DOC_STATUS || ''),
      url: `/api/startTask/doc-cust-save/${Number(d.saveId ?? d.SAVE_ID ?? 0)}/file`,
    }));
  }

  /** L_START_TASK.TASK_DATA 에 저장된 E-Filing 편집 모델 + 문서 목록 로드 */
  /** 짧은 문자열 지문(djb2) — prefill 원본 변경 감지용 */
  function hashString(s: string): string {
    let h = 5381;
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    return `${s.length}:${(h >>> 0).toString(36)}`;
  }

  async function fetchSavedState(taskId: number | string): Promise<any | null> {
    const id = Number(taskId);
    if (!id) return null;
    try {
      return await getJson(`/api/aiDocuments/incorporation/load-state/${id}`);
    } catch (e) {
      console.warn('[IncorporationEfiling] load-state failed:', e);
      return null;
    }
  }

  /**
   * 현재 편집 모델을 L_START_TASK.TASK_DATA.efilingModel 에 저장.
   * 업로드 파일 base64(contentBase64)는 암호화 컬럼 비대화 방지를 위해 제외 — 폼 데이터만 영속화.
   */
  async function saveEfilingModel(taskId: number | string): Promise<void> {
    const id = Number(taskId);
    if (!id) return;
    const lean = JSON.parse(
      JSON.stringify(state.model, (k, v) => (k === 'contentBase64' ? undefined : v)),
    );
    try {
      await putJson(`/api/aiDocuments/incorporation/save-state/${id}`, { efilingModel: lean });
    } catch (e) {
      console.warn('[IncorporationEfiling] save-state failed:', e);
    }
  }

  /**
   * Load everything for the review screen.
   *  - 최초 진입: L_INCORPORATIONS 에서 시드 → TASK_DATA.efilingModel 에 저장
   *  - 이후: TASK_DATA.efilingModel 을 그대로 사용(인코퍼레이션 테이블 안 봄)
   */
  /**
   * @param opts.prefillForm  icpCd(setup-entity 인코퍼레이션) 없이, 이 SgIncorporationForm 로
   *   모델을 시드한다. 커스텀폼 기반 My Preset(예: Singbac Incorporation Pre) 이 동일한
   *   E-Filing 화면을 자동채움으로 쓰기 위한 경로. 저장된 편집본이 있으면 그게 우선.
   */
  async function load(opts: {
    icpCd: string;
    taskId?: number | string;
    prefillForm?: SgIncorporationForm | null;
    companyName?: string;
  }): Promise<void> {
    const icpCd = String(opts.icpCd || '').trim();
    const taskId = opts.taskId ?? 0;
    const prefillForm = opts.prefillForm || null;
    state.icpCd = icpCd;
    state.loading = true;
    state.error = '';
    try {
      // prefillForm 경로는 icpCd 없이도 진행(커스텀폼 기반). 둘 다 없을 때만 오류.
      if (!icpCd && !prefillForm) throw new Error('Incorporation (icpCd) is missing.');
      codeMaps.value = await loadCodeMaps();

      const saved = await fetchSavedState(taskId);
      if (saved) applyGeneratedDocs(saved);

      // 커스텀폼(prefill) 경로 — 편집본을 만든 원본(작업 문서 값)의 지문을 함께 저장해 두고,
      //   작업을 Update 해 원본이 바뀌었으면 편집본 대신 새 원본으로 다시 시드한다(새 컨트롤러 등 반영).
      const prefillSig = prefillForm ? hashString(JSON.stringify(prefillForm)) : '';
      const prefillChanged =
        !!prefillForm && !!saved?.efilingModel && (saved.efilingModel as any).__prefillSig !== prefillSig;

      if (saved?.efilingModel && !prefillChanged) {
        // 저장된 편집본 복원 — 소스(인코퍼레이션/커스텀폼)는 보지 않는다.
        state.model = saved.efilingModel as IncorporationEfilingModel;
        state.companyName =
          String(state.model.entityInfo?.companyName || '').trim() ||
          String(saved.taskTitle || '').trim();
      } else if (prefillForm) {
        // 최초 진입(커스텀폼) — SgIncorporationForm 으로 시드 후 저장 컬럼에 영속화.
        const companyName =
          String(opts.companyName || '').trim() || String(prefillForm.company?.entityName || '').trim();
        state.companyName = companyName;
        state.model = seedEfilingModel(prefillForm, { companyName });
        (state.model as any).__prefillSig = prefillSig;
        await saveEfilingModel(taskId);
      } else {
        // 최초 진입(setup-entity) — 인코퍼레이션 테이블에서 시드 후 저장 컬럼에 영속화.
        await loadEntityData(icpCd);
        await saveEfilingModel(taskId);
      }

      // 업로드 파일 목록은 유효한 icpCd 가 있을 때만(커스텀폼 경로는 인코퍼레이션 파일 없음).
      if (icpCd) await loadUploadedFiles(icpCd);
      state.loaded = true;
    } catch (e: any) {
      state.error = e?.message || 'Failed to load incorporation data.';
      console.error('[IncorporationEfiling] load failed:', e);
    } finally {
      state.loading = false;
    }
  }

  /** Uploaded files that belong to a given owner (e.g. a controller fileCd). */
  function filesForOwner(ownerCd: string): EfUploadedFile[] {
    const cd = String(ownerCd || '').trim();
    if (!cd) return [];
    return state.uploadedFiles.filter((f) => f.fileOwnerCd === cd);
  }

  /** Uploaded files of a given type (e.g. ALLOT_DOC). */
  function filesForType(fileType: string): EfUploadedFile[] {
    return state.uploadedFiles.filter((f) => f.fileType === fileType);
  }

  /** bizfile create 페이로드 — 코드(B03/B04/B05) → BIGO 값 변환 후 조립. */
  function buildPayload(): Record<string, unknown> {
    const m = codeMaps.value
      ? convertCodesForPayload(state.model, codeMaps.value)
      : state.model;
    return buildBizfileCreatePayload(m);
  }

  return {
    state,
    rawIncorporationData,
    load,
    saveEfilingModel,
    filesForOwner,
    filesForType,
    buildPayload,
  };
}
